import { ref } from 'vue'
import { loadExternalScript } from '../shared/utils'

// Module singleton: Maps JS loads once per page. State drives whether
// DeliveryFields renders the autocomplete or falls back to classic fields.
//
// 'degraded' is terminal for the session: once Google fails at runtime (no
// billing, timeout, referrer block, API error) we stay on classic fields so
// checkout is never blocked by a problem the user can't fix.

const LOAD_TIMEOUT_MS = 10000

// Hard ceiling on billable autocomplete sessions per page load. A stuck key,
// a bot, or pathological typing can't run up the Google bill past this — we
// degrade to manual fields instead.
const MAX_SESSIONS_PER_PAGE = 12

const state = ref('idle') // idle | loading | ready | degraded
let loadPromise = null
let sessionCount = 0

function mapsUrl(apiKey) {
  return `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
}

function isApiReady() {
  return (
    typeof google !== 'undefined' &&
    google.maps?.places?.AutocompleteSessionToken &&
    google.maps?.places?.AutocompleteSuggestion
  )
}

export function useAddressAutocomplete() {
  // Caller signals a runtime failure (e.g. AddressAutocomplete's `error` emit).
  function degrade(reason) {
    if (state.value !== 'degraded') {
      console.warn('[ezcl] address autocomplete degraded → manual fields:', reason)
      state.value = 'degraded'
    }
  }

  function load(apiKey) {
    if (!apiKey) {
      degrade('no api key')
      return Promise.resolve(state.value)
    }
    if (loadPromise) return loadPromise

    state.value = 'loading'
    loadPromise = new Promise((resolve) => {
      const watchdog = setTimeout(() => {
        degrade('load timeout')
        resolve(state.value)
      }, LOAD_TIMEOUT_MS)

      loadExternalScript(mapsUrl(apiKey))
        .then(() => {
          clearTimeout(watchdog)
          if (isApiReady()) {
            state.value = 'ready'
          } else {
            degrade('Places API unavailable after load')
          }
          resolve(state.value)
        })
        .catch((err) => {
          clearTimeout(watchdog)
          degrade(err)
          resolve(state.value)
        })
    })
    return loadPromise
  }

  // Called by the component when it opens a new billable session. Returns
  // false (and degrades) once the per-page ceiling is hit.
  function allowSession() {
    if (state.value === 'degraded') return false
    if (sessionCount >= MAX_SESSIONS_PER_PAGE) {
      degrade('session ceiling reached')
      return false
    }
    sessionCount += 1
    return true
  }

  return { state, load, degrade, isApiReady, allowSession }
}
